import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaPickerComponent,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowQuestionType,
} from '@gripp/shared-logic';
import { useEffect } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../../../themes/colors';
import { useFormContext } from '../../../forms/formContext';
import { RoutineAdminInputText } from '../../components/routineAdminInputText';
import { RoutineQuestionModules } from '../questions/routineQuestionModules';

type WorkflowAdminQuestionProps = {
  question: WorkflowQuestionConfig;
  index: number;
  control: Control;
  type: WorkflowQuestionType;
  pagePrefix: string;
  mediaPicker?: MediaPickerComponent;
};

const HEADLINE_ELIGIBLE_TYPES = new Set<WorkflowQuestionType>([
  WorkflowQuestionType.Text,
  WorkflowQuestionType.Counter,
  WorkflowQuestionType.Status,
  WorkflowQuestionType.QuickConfirm,
  WorkflowQuestionType.Radio,
  WorkflowQuestionType.Checkbox,
  WorkflowQuestionType.Media,
  WorkflowQuestionType.Time,
  WorkflowQuestionType.BarcodeScanner,
]);

export const WorkflowAdminQuestion = (props: WorkflowAdminQuestionProps) => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const { question, index, control, type: questionType, pagePrefix } = props;
  const i18nPrefix = 'routines.workflow.admin.question';
  const indexPrefix = `${pagePrefix}.questions.${index}`;
  const questionOrder = index + 1;
  const QuestionAdmin =
    RoutineQuestionModules[questionType].adminForm ?? (() => null);
  const watchedIsHeadline = useWatch({
    control,
    name: `${indexPrefix}.isHeadline`,
  });
  const isHeadline = !!watchedIsHeadline;
  const watchedIdpManaged = useWatch({
    control,
    name: `${indexPrefix}.idpManaged`,
  });
  /**
   * IDP management is a Text-only affordance in v1 — the API's enqueue planner
   * requires `type === TEXT` too, so a flag left behind on a question whose type
   * was changed is ignored here rather than treated as managed.
   */
  const isIdpManaged =
    questionType === WorkflowQuestionType.Text && !!watchedIdpManaged;
  /**
   * A managed field is hidden from operators and filled asynchronously, so it can
   * never be the Activity headline — there is nothing to show at submit time.
   */
  const isHeadlineEligible =
    HEADLINE_ELIGIBLE_TYPES.has(questionType) && !isIdpManaged;

  useEffect(() => {
    if (!isIdpManaged || !isHeadline) return;
    setValue(`${indexPrefix}.isHeadline`, false, { shouldDirty: true });
  }, [isIdpManaged, isHeadline]);

  const onToggleHeadline = () => {
    if (!isHeadlineEligible) return;
    const pages: WorkflowPageConfig[] = getValues('pages') ?? [];
    // Clear isHeadline from all questions across all pages
    pages.forEach((page: WorkflowPageConfig, pageIndex: number) => {
      page.questions?.forEach((q: WorkflowQuestionConfig, qIndex: number) => {
        if (q.isHeadline) {
          setValue(`pages.${pageIndex}.questions.${qIndex}.isHeadline`, false, {
            shouldDirty: true,
          });
        }
      });
    });
    // Set the current question as headline (unless it was already on)
    if (!isHeadline) {
      setValue(`${indexPrefix}.isHeadline`, true, { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (question.order === questionOrder) return;
    setValue(`${indexPrefix}.name`, `Question ${questionOrder}`);
    setValue(`${indexPrefix}.order`, questionOrder);
  }, [questionOrder]);

  return (
    <>
      <View style={styles.headerRow}>
        <Text style={styles.header}>
          {t(`${i18nPrefix}.question`, { question: index + 1 })}
        </Text>
        <Text style={[styles.header, styles.questionType]}>
          {t(`routines.workflow.questionTypes.${questionType}`)}
        </Text>
        {isHeadlineEligible && (
          <Pressable onPress={onToggleHeadline} style={styles.headlineToggle}>
            <MaterialCommunityIcons
              name={isHeadline ? 'star' : 'star-outline'}
              color={isHeadline ? Colors.primary : Colors.role.borderStrong}
              size={22}
            />
          </Pressable>
        )}
      </View>
      <RoutineAdminInputText
        control={control}
        name="instructions"
        i18nPrefix={i18nPrefix}
        indexPrefix={indexPrefix}
        textArea={true}
        mediaPicker={props.mediaPicker}
      />
      <QuestionAdmin
        config={question}
        control={control}
        indexPrefix={indexPrefix}
      />
    </>
  );
};

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  header: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.role.textStrong,
  },
  questionType: {
    flexGrow: 1,
    textAlign: 'right',
    marginRight: 10,
  },
  headlineToggle: {
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
});
